import { ICommand } from './ICommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { Edge, Node } from '../models/scene';
import { expandNodeIdsWithGroupMembers } from '../../lib/diagram/grouping';
import { ShapeMapAdapter } from '../../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../../lib/diagram/shapeGeometry';

export class CutNodesCommand implements ICommand {
  id: string;
  name = 'Cut Nodes';
  description: string;
  timestamp = Date.now();

  private nodeIds: string[];
  private removedNodes: Node[] = [];
  private removedEdges: Edge[] = [];

  constructor(nodeIds: string[]) {
    this.id = `cut-nodes-${Date.now()}`;
    this.nodeIds = nodeIds;
    this.description = nodeIds.length === 1 ? 'Cut node' : `Cut ${nodeIds.length} nodes`;
  }

  execute(): void {
    const store = useDiagramStore.getState();
    const mutationManager = getNodeMutationManager();

    const expandedIds = expandNodeIdsWithGroupMembers(this.nodeIds, store.nodes);
    this.nodeIds = Array.from(expandedIds);
    this.removedNodes = store.nodes.filter(n => expandedIds.has(n.id));
    this.removedEdges = store.edges.filter(edge => expandedIds.has(edge.from) || expandedIds.has(edge.to));

    // Delete through Yjs/mutation manager to keep source of truth in sync
    const ydoc = (mutationManager as any).ydoc;
    if (ydoc && (this.removedEdges.length > 0 || this.nodeIds.length > 0)) {
      const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(ydoc.getMap('shapes')));
      const connectorsMap = ydoc.getMap('connectors');

      ydoc.transact(() => {
        this.removedEdges.forEach(edge => {
          connectorsMap.delete(edge.id);
        });
        this.nodeIds.forEach(nodeId => {
          shapes.delete(nodeId);
        });
      }, 'local');
    } else {
      if (this.removedEdges.length > 1) {
        (mutationManager as any).batchDeleteEdges(this.removedEdges.map(e => e.id));
      } else if (this.removedEdges.length === 1) {
        mutationManager.deleteEdge(this.removedEdges[0].id);
      }

      if (this.nodeIds.length > 1) {
        (mutationManager as any).batchDeleteNodes(this.nodeIds);
      } else if (this.nodeIds.length === 1) {
        mutationManager.deleteNode(this.nodeIds[0]);
      }
    }

    store.setSelected(new Set());
  }

  undo(): void {
    const mutationManager = getNodeMutationManager();

    if (this.removedNodes.length > 1) {
      (mutationManager as any).batchAddNodes(this.removedNodes);
    } else if (this.removedNodes.length === 1) {
      mutationManager.addNode(this.removedNodes[0]);
    }

    if (this.removedEdges.length > 1) {
      (mutationManager as any).batchAddEdges(this.removedEdges);
    } else if (this.removedEdges.length === 1) {
      mutationManager.addEdge(this.removedEdges[0]);
    }

    const store = useDiagramStore.getState();
    store.setSelected(new Set(this.removedNodes.map(n => n.id)));
  }

  redo(): void {
    this.execute();
  }
}
